import { useCallback, useState } from "react"

import type { FieldGroupEnum, ReferenceTableName } from "@/graphql/codegen/graphql"
import { useFileUpload } from "@/hooks/useFileUpload"

export enum PDFSize {
  Small = "small",
  Medium = "medium",
  Large = "large",
  ExtraLarge = "extraLarge",
}

interface UsePDFUploadProps {
  referenceTableName: ReferenceTableName
  getReferenceId: () => string
}

type UploadState = Record<FieldGroupEnum, boolean>

interface PDFUploadHook {
  uploading: UploadState
  uploadFile: (file: File, type: FieldGroupEnum, label: string) => Promise<boolean>
  validatePDFFile: (file: File) => boolean
}

export const usePDFUpload = ({ referenceTableName, getReferenceId }: UsePDFUploadProps): PDFUploadHook => {
  const { uploadFile: baseUploadFile } = useFileUpload()
  const [uploading, setUploading] = useState<UploadState>({} as UploadState)

  const uploadFile = useCallback(
    async (file: File, type: FieldGroupEnum, label: string) => {
      if (file.type !== "application/pdf") {
        throw new Error("Only PDF files are allowed.")
      }

      if (file.size > 10 * 1024 * 1024) {
        throw new Error("File size should not exceed 10MB.")
      }

      setUploading((prev) => ({ ...prev, [type]: true }))

      try {
        const success = await baseUploadFile({
          file,
          type: file.type,
          metadata: {
            fieldGroup: type,
            referenceId: getReferenceId(),
            referenceTableName,
          },
          label,
        })

        return success
      } catch (error) {
        console.error("Failed to upload PDF:", error)
        return false
      } finally {
        setUploading((prev) => ({ ...prev, [type]: false }))
      }
    },
    [baseUploadFile, getReferenceId, referenceTableName]
  )

  const validatePDFFile = useCallback((file: File): boolean => {
    if (file.type !== "application/pdf") {
      throw new Error("Only PDF files are allowed.")
    }

    if (file.size > 10 * 1024 * 1024) {
      throw new Error("File size should not exceed 10MB.")
    }

    return true
  }, [])

  return {
    uploading,
    uploadFile,
    validatePDFFile,
  }
}
